// Dev-only prompt-var fixtures + their dispatcher, split out of `mocks.ts` on the
// `dashboard-mocks` precedent. Absent entirely until now, so
// `/org/:org/account/prompt-vars` rendered `<ErrorState/>` under `PUBLIC_MOCKS`.
//
// THE ADDRESS IS SPLIT PATH/QUERY exactly as the route splits it —
// `/v1/prompt-vars/:scope/:key?ref=` — because a `ref` is a repo id or a template
// NAME and may contain a slash, which no path segment could carry. A mock that
// read the second segment as the ref (the obvious wrong guess) would answer the
// wrong row the moment a template's name had a slash in it.
//
// STATEFUL, because the write plane's whole story is that changing the ADDRESS
// writes a DIFFERENT row rather than editing one in place. That is only
// observable if a save persists and the re-read sees it.

import { at } from './mock-session';
import type { ApiPromptVar, PromptVarScope } from './prompt-vars';
import { unhandledMockRoute } from './unhandled-mock-route';

/**
 * One var per scope, general to specific, and `value` is a STRING, a BOOLEAN and
 * an OBJECT — arbitrary JSON is what the column holds, and a fixture of three
 * strings would let a client that quietly stringifies everything look correct.
 */
const vars: ApiPromptVar[] = [
  {
    id: 'pvr_mock01',
    scope: 'account',
    // `null`, never `''` — the account scope takes no ref and the API refuses one.
    ref: null,
    key: 'house_style',
    value: 'Small diffs. Tests first. No drive-by reformatting.',
    created_iso: at('2026-06-04'),
    updated_iso: at('2026-06-04'),
  },
  {
    id: 'pvr_mock02',
    scope: 'repo',
    ref: 'repo_1',
    key: 'strict_linting',
    value: true,
    created_iso: at('2026-06-18'),
    updated_iso: at('2026-07-09'),
  },
  {
    id: 'pvr_mock03',
    scope: 'template',
    ref: 'claude',
    key: 'limits',
    value: { max_files: 20, max_lines: 400 },
    created_iso: at('2026-07-01'),
    updated_iso: at('2026-07-01'),
  },
];

/** The `(scope, ref, key)` address, with `''` and `null` the same absent ref. */
const sameAddress = (row: ApiPromptVar, scope: string, ref: string | null, key: string): boolean =>
  row.scope === scope && (row.ref ?? '') === (ref ?? '') && row.key === key;

/**
 * Handle a `/v1/prompt-vars*` request, or return `null` if `path` is not a
 * prompt-var route (so `mockFetch` falls through). A PUT UPSERTS by address (so
 * an unknown one creates) and a DELETE is idempotent — neither may reject on an
 * address nobody holds, which is what the route itself does.
 */
export function promptVarsMock(
  path: string,
  method: string,
  init?: RequestInit,
): Promise<unknown> | null {
  const [bare = path, query = ''] = path.split('?');
  if (bare !== '/v1/prompt-vars' && !bare.startsWith('/v1/prompt-vars/')) return null;
  const reject = (): Promise<never> => unhandledMockRoute(method, path);
  const params = new URLSearchParams(query);
  // An empty `?ref=` is treated as ABSENT, the way the route treats it.
  const ref = params.get('ref') === '' ? null : params.get('ref');

  if (bare === '/v1/prompt-vars') {
    if (method !== 'GET') return reject();
    const scope = params.get('scope');
    const rows = vars.filter(
      (v) => (scope === null || v.scope === scope) && (ref === null || v.ref === ref),
    );
    // Whole collection, so `next_cursor: null` is the completeness assertion.
    return Promise.resolve({ data: rows, next_cursor: null });
  }

  const [scope = '', key = ''] = bare
    .slice('/v1/prompt-vars/'.length)
    .split('/')
    .map((s) => decodeURIComponent(s));
  const i = vars.findIndex((v) => sameAddress(v, scope, ref, key));
  if (method === 'GET') {
    const row = vars[i];
    return row === undefined ? reject() : Promise.resolve({ var: row });
  }
  if (method === 'PUT') return Promise.resolve({ var: upsert(scope, ref, key, i, init) });
  if (method === 'DELETE') {
    if (i >= 0) vars.splice(i, 1);
    // 204 with an EMPTY body, set or not.
    return Promise.resolve(undefined);
  }
  return reject();
}

/** Upsert by `(scope, ref, key)`. `value` passes through VERBATIM — it is any JSON. */
function upsert(
  scope: string,
  ref: string | null,
  key: string,
  i: number,
  init?: RequestInit,
): ApiPromptVar {
  const body = init?.body ? (JSON.parse(String(init.body)) as { value?: unknown }) : {};
  const row: ApiPromptVar = {
    id: vars[i]?.id ?? `pvr_new_${vars.length + 1}`,
    scope: scope as PromptVarScope,
    ref,
    key,
    value: body.value ?? null,
    created_iso: vars[i]?.created_iso ?? at('2026-07-20'),
    updated_iso: at('2026-07-20'),
  };
  if (i >= 0) vars[i] = row;
  else vars.push(row);
  return row;
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
const PRISTINE_VARS = vars.map((row) => structuredClone(row));

export function resetPromptVarsMocks(): void {
  vars.splice(0, vars.length, ...PRISTINE_VARS.map((row) => structuredClone(row)));
}
